// 3rd party libraries
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

// Components
import { Button, Modal } from 'components';
import { StyledTableForm, StyledTableInput } from './Form';
import { Dropdown } from './Dropdown';

//Context
import { useWidgetTableContext } from './WidgetTableContext';
import { CreateNewWidgetItemProps, WidgetTableDropdownItem } from 'types/machine-health';

// Types
import { ModalSize } from 'types';
import { WidgetType } from 'types/protein';
import FileUpload from 'common/components/FileUpload';

interface StyledContentProps {
  padding?: string | undefined;
}

// Styled components
const StyledContent = styled.div<StyledContentProps>`
  display: flex;
  flex-direction: column;
  font-size: 0.75rem;
  gap: 1rem;
  padding: ${(props) => (props.padding ? props.padding : '0.25rem 3rem')};
`;

const StyledLabel = styled.label``;

const StyledInputGroup = styled.div`
  align-items: center;
  display: flex;
  gap: 1rem;
  width: 100%;
`;

const StyledButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  width: 100%;
`;

interface TableModalProps {
  hideFields: {
    [key: string]: boolean;
  };
  modalType: WidgetType;
  options?: WidgetTableDropdownItem[];
  parentId?: string;
  submitCallback?: ({ value, widgetType, tabId, isTag }: CreateNewWidgetItemProps) => void;
  tabId: string;
  onFileUpdate?: (file: File | null) => void; // Prop to notify parent about the uploaded file
  tagImageId?: string;
}

const TableModal = ({
  hideFields,
  modalType,
  options,
  submitCallback,
  tabId,
  onFileUpdate,
  tagImageId
}: TableModalProps): JSX.Element => {
  const {
    isModalOpenById,
    modalTitle,
    setIsModalOpenById,
    setModalTitle,
    setTagGroupParentId,
    tagGroupParentId,
    setUploadTagsImage,
  } = useWidgetTableContext();
  const [isDisabled, setIsDisabled] = useState<{ [key: string]: boolean }>({});
  const [widgetType, setWidgetType] = useState<WidgetTableDropdownItem | undefined>();
  const [value, setValue] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    if (!hideFields.name) {
      setIsDisabled((prev) => ({
        ...prev,
        name: value ? false : true
      }));
    } else {
      setIsDisabled((prev) => ({
        ...prev,
        name: false
      }));
    }

    if (!hideFields.type) {
      setIsDisabled((prev) => ({
        ...prev,
        type: widgetType ? false : true
      }));
    } else {
      setIsDisabled((prev) => ({
        ...prev,
        type: false
      }));
    }
  }, [hideFields, value, widgetType]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCancel(null);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const clearModalState = () => {
    setIsModalOpenById(null);
    setModalTitle('');
    setValue('');
    setWidgetType(undefined);
    setTagGroupParentId(undefined);
    setSelectedFile(null);
  };

  const handleSubmit = (
    e:
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
      | React.KeyboardEvent<HTMLButtonElement>
      | null
  ) => {
    e?.preventDefault();

    if (modalType === WidgetType.UploadMatrixImageRow && selectedFile) {
      onFileUpdate?.(selectedFile); // Notify parent when Upload button is clicked
      submitCallback?.({
        file: selectedFile, // Pass the selected file
        widgetType: WidgetType.UploadMatrixImageRow,
        tabId,
        value: selectedFile.name
      });

      setUploadTagsImage(prev =>
        prev.map(item =>
          item.id === tagImageId ? { 
            ...item, 
            imageFile:selectedFile,
            imageSize:undefined,
            imageUrl:undefined, 
            markedForDeleted: false 
          } : item
        )
      );

    } else {
      submitCallback?.({
        isTag: false,
        parentId: tagGroupParentId,
        tabId,
        value: value || '',
        widgetType:
          modalType === WidgetType.TagGroupRow ? WidgetType.TagGroup : WidgetType.MatrixWidget
      });
    }
    clearModalState();
  };

  const handleCancel = (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | null) => {
    e?.preventDefault();
    clearModalState();
  };

  const handleFileChange = (file: File | null) => {
    setSelectedFile(file); // Only update local state
    setValue(file?.name || '');
  };

  return (
    <Modal
      onClose={() => setIsModalOpenById(null)}
      showCloseButton={modalType === WidgetType.UploadMatrixImageRow ? true : false}
      size={ModalSize.SMALL_AUTO_HEIGHT}
      title={modalTitle}
      visible={isModalOpenById === tabId || tagImageId === isModalOpenById}
      zIndex={999}
      {...(modalType === WidgetType.UploadMatrixImageRow ? { widthOverride: '30rem' } : {})}
    >
      <StyledContent padding={modalType === WidgetType.UploadMatrixImageRow ? '1rem' : undefined}>
        <StyledTableForm isVertical>
          {modalType !== WidgetType.UploadMatrixImageRow && (
            <>
              {!hideFields.name && (
                <StyledInputGroup>
                  <StyledLabel htmlFor="new-item-name">Name</StyledLabel>
                  <StyledTableInput
                    id="new-item-name"
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={(e) => handleInputKeyDown(e)}
                    type="text"
                    value={value}
                  />
                </StyledInputGroup>
              )}
              {!hideFields.type && (options?.length || 0) > 0 && (
                <StyledInputGroup>
                  <StyledLabel htmlFor="new-item-type">Type</StyledLabel>

                  <Dropdown
                    onChangeCallback={setWidgetType}
                    options={options as WidgetTableDropdownItem[]}
                  />
                </StyledInputGroup>
              )}
            </>
          )}
          {modalType === WidgetType.UploadMatrixImageRow && (
            <FileUpload
              className="image-selector"
              acceptedTypes={{
                'application/image': ['.jpeg', '.webp', '.png', '.jpg']
              }}
              acceptedFileText=".jpg, .png, File size (10 MB max)."
              maxFileSize={10485760}
              onFileChange={handleFileChange}
            />
          )}
          <StyledButtonGroup>
            <Button
              width="auto"
              onClick={(e) => handleCancel(e)}
              onKeyDown={(e) => handleKeyDown(e)}
              variant="outline-blue"
              height="2.5rem"
            >
              Cancel
            </Button>
            <Button
              disabled={modalType === WidgetType.UploadMatrixImageRow && !selectedFile}
              onClick={handleSubmit}
              onKeyDown={(e) => handleSubmit(e)}
              variant="filled-primary"
              width="auto"
              height="2.5rem"
            >
              {modalType === WidgetType.UploadMatrixImageRow ? 'Upload' : 'Create'}
            </Button>
          </StyledButtonGroup>
        </StyledTableForm>
      </StyledContent>
    </Modal>
  );
};

export default TableModal;
